import { safe } from '../../documentos/commercialExcelUtils';
import { htPdfColWidthsMm } from './htPdfColumns';
import { htPdfDrawFullWidthBlockFrame, htPdfDrawFullWidthRowFrame } from './htPdfFullWidthFrame';
import { drawHtModalitySectionPdf } from './drawHtModalityPdf';
import { drawHtSectionTitleRowPdf } from './drawHtSectionTitleRowPdf';
import { drawHtFooterPdf } from './drawHtFooterPdf';
import { drawHtShippingBlockPdf } from './drawHtShippingBlockPdf';
import { drawHtTreatmentBlockPdf } from './drawHtTreatmentBlockPdf';
import { htBiosetaLogo, HT_BIOSETA_LOGO_ASPECT } from './htSheetImages';

const FONT = 7;
const FONT_SM = 6.2;
const LINE_TIGHT = 2.55;
const PAD = 1.2;
/** Margen vertical en bloques etiqueta | valor (exportador, importador, origen, destino). */
const PAIR_PAD = 2.85;
const INTRO_PAD_TOP = 5.5;
/** Alturas filas 19–26 plantilla Invoice 5019 (pt → mm). */
const EXCEL_PT_TO_MM = 25.4 / 72;
const HT_PROD_ROW19_MM = 14.55 * EXCEL_PT_TO_MM;
const HT_PROD_ROWS20_26_MM = (13.05 * 7 * EXCEL_PT_TO_MM) * 0.5;

function drawRect(doc, x, y, w, h) {
  doc.rect(x, y, w, h);
}

function paragraphHeight(doc, text, maxW, lineH = LINE_TIGHT) {
  const lines = doc.splitTextToSize(safe(text), maxW);
  return lines.length * lineH;
}

function drawParagraph(doc, text, x, y, maxW, lineH = LINE_TIGHT) {
  const lines = doc.splitTextToSize(safe(text), maxW);
  doc.text(lines, x, y);
  return y + lines.length * lineH;
}

function labelValueLineH(doc) {
  return Math.max(LINE_TIGHT, lineHeightMm(doc));
}

/** Párrafo en celda: `yTop` es el borde superior del contenido (baseline top). */
function drawParagraphFromTop(doc, text, x, yTop, maxW, lineH) {
  const lh = lineH ?? labelValueLineH(doc);
  const lines = doc.splitTextToSize(safe(text), maxW);
  doc.text(lines, x, yTop, { baseline: 'top', maxWidth: maxW });
  return yTop + lines.length * lh;
}

function labelBlockHeight(doc, label1, label2, wLabel, lh) {
  let h = paragraphHeight(doc, label1, wLabel, lh);
  if (label2) h += paragraphHeight(doc, label2, wLabel, lh) + 0.35;
  return h;
}

function drawHtIntroPdf(doc, S, x, y, maxW) {
  let cy = y + INTRO_PAD_TOP;
  cy = drawParagraph(doc, S.HT_INTRO_PT, x, cy, maxW);
  cy += LINE_TIGHT;
  cy = drawParagraph(doc, S.HT_INTRO_EN, x, cy, maxW);
  return cy + 0.8;
}

function measureHtIntroPdf(doc, S, maxW) {
  let h = INTRO_PAD_TOP;
  h += paragraphHeight(doc, S.HT_INTRO_PT, maxW);
  h += LINE_TIGHT;
  h += paragraphHeight(doc, S.HT_INTRO_EN, maxW);
  return h + 1;
}

function colStarts(margin, cols) {
  const xs = [margin];
  for (let i = 0; i < cols.length; i += 1) xs.push(xs[i] + cols[i]);
  return xs;
}

function lineHeightMm(doc) {
  const fontSize = doc.internal.getFontSize();
  return (fontSize * doc.getLineHeightFactor()) / doc.internal.scaleFactor;
}

/** Centrado horizontal y vertical; opcional una sola línea (cabeceras). */
function drawCenteredInBox(doc, text, x, y, w, h, opts = {}) {
  const maxW = w - PAD * 2;
  let content = safe(text).trim();
  if (opts.singleLine) content = content.replace(/\s*\n+\s*/g, ' / ').replace(/\s+/g, ' ').trim();
  const lines = opts.singleLine
    ? [content]
    : doc.splitTextToSize(content, maxW);
  const lh = lineHeightMm(doc);
  const blockH = lines.length * lh;
  const cx = x + w / 2;
  const midY = y + h / 2;
  if (lines.length === 1) {
    let fs = doc.internal.getFontSize();
    while (fs > 4.8) {
      doc.setFontSize(fs);
      if (doc.splitTextToSize(lines[0], maxW).length <= 1) break;
      fs -= 0.25;
    }
    doc.text(lines[0], cx, midY, { align: 'center', baseline: 'middle', maxWidth: maxW });
    if (opts.restoreFontSize) doc.setFontSize(opts.restoreFontSize);
    return;
  }
  const firstBaseline = midY - blockH / 2 + lh * 0.82;
  lines.forEach((line, i) => {
    doc.text(line, cx, firstBaseline + i * lh, { align: 'center', maxWidth: maxW });
  });
}

function drawProductsSectionPdf(doc, params, S, xs, y) {
  const x0 = xs[0];
  const xSplitQty = xs[3];
  const xEnd = xs[5];
  const wDescCol = xSplitQty - x0;
  const wQtyCol = xEnd - xSplitQty;
  const h19 = HT_PROD_ROW19_MM;
  const blockH = HT_PROD_ROWS20_26_MM;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SM);
  const hdrOpts = { singleLine: true, restoreFontSize: FONT_SM };

  htPdfDrawFullWidthRowFrame(doc, xs, y, h19, 19, [xSplitQty]);
  drawCenteredInBox(doc, S.HT_PRODUCTS_HEADER_LINE, x0, y, wDescCol, h19, hdrOpts);
  doc.setFontSize(FONT_SM);
  drawCenteredInBox(doc, S.HT_QTY_HEADER_LINE, xSplitQty, y, wQtyCol, h19, hdrOpts);
  doc.setFontSize(FONT_SM);
  y += h19;

  htPdfDrawFullWidthBlockFrame(doc, xs, y, blockH, 20, 26, [xSplitQty]);
  drawCenteredInBox(doc, S.HT_PRODUCTS_VALUE, x0, y, wDescCol, blockH);
  drawCenteredInBox(doc, params.quantityProducts, xSplitQty, y, wQtyCol, blockH);

  return y + blockH;
}

function labelValueBlockHeight(doc, label1, label2, value, wLabel, wValue) {
  const lh = labelValueLineH(doc);
  const labelH = labelBlockHeight(doc, label1, label2, wLabel, lh);
  const valueH = paragraphHeight(doc, value, wValue, lh);
  return Math.max(labelH, valueH) + PAIR_PAD * 2;
}

function drawLabelValuePairPdf(doc, xs, y, rowLabel1, rowLabel2, label1, label2, value) {
  const x0 = xs[0];
  const xSplit = xs[2];
  const xLabel = x0 + PAD;
  const xValue = xSplit + PAD;
  const wLabel = xSplit - x0 - PAD * 2;
  const wValue = xs[5] - xSplit - PAD * 2;

  const blockH = labelValueBlockHeight(doc, label1, label2, value, wLabel, wValue);

  htPdfDrawFullWidthBlockFrame(doc, xs, y, blockH, rowLabel1, rowLabel2, [xSplit]);

  const lh = labelValueLineH(doc);
  const labelH = labelBlockHeight(doc, label1, label2, wLabel, lh);
  const valueH = paragraphHeight(doc, value, wValue, lh);
  const contentH = Math.max(labelH, valueH);
  const yLabel = y + PAIR_PAD + (contentH - labelH) / 2;
  const yValue = y + PAIR_PAD + (contentH - valueH) / 2;

  let ty = drawParagraphFromTop(doc, label1, xLabel, yLabel, wLabel, lh);
  if (label2) drawParagraphFromTop(doc, label2, xLabel, ty + 0.35, wLabel, lh);
  drawParagraphFromTop(doc, value, xValue, yValue, wValue, lh);

  return y + blockH;
}

function drawLabelValueSinglePdf(doc, xs, y, excelRow, label, value) {
  const x0 = xs[0];
  const xSplit = xs[2];
  const xLabel = x0 + PAD;
  const xValue = xSplit + PAD;
  const wLabel = xSplit - x0 - PAD * 2;
  const wValue = xs[5] - xSplit - PAD * 2;

  const h = labelValueBlockHeight(doc, label, null, value, wLabel, wValue);

  htPdfDrawFullWidthRowFrame(doc, xs, y, h, excelRow, [xSplit]);

  const lh = labelValueLineH(doc);
  const labelH = paragraphHeight(doc, label, wLabel, lh);
  const valueH = paragraphHeight(doc, value, wValue, lh);
  const contentH = Math.max(labelH, valueH);
  const yLabel = y + PAIR_PAD + (contentH - labelH) / 2;
  const yValue = y + PAIR_PAD + (contentH - valueH) / 2;

  drawParagraphFromTop(doc, label, xLabel, yLabel, wLabel, lh);
  drawParagraphFromTop(doc, value, xValue, yValue, wValue, lh);
  return y + h;
}

function drawLabelValueRowsPdf(doc, params, S, xs, y) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SM);

  y = drawLabelValuePairPdf(
    doc,
    xs,
    y,
    27,
    28,
    S.HT_EXPORTADOR_LABEL,
    S.HT_EXPORTADOR_LABEL_EN,
    S.HT_EXPORTADOR_VALUE
  );
  y = drawLabelValuePairPdf(
    doc,
    xs,
    y,
    29,
    30,
    S.HT_IMPORTADOR_LABEL,
    S.HT_IMPORTADOR_LABEL_EN,
    params.consigneeAddress
  );
  y = drawLabelValueSinglePdf(doc, xs, y, 31, S.HT_ORIGIN_LABEL, params.origin);
  y = drawLabelValueSinglePdf(doc, xs, y, 32, S.HT_DEST_LABEL, params.destination);
  return y;
}

/**
 * Certificado HT completo (filas 5–65), sin autoTable.
 * @returns {number} Y final del certificado (fila 65)
 */
export function drawHtPart1Pdf(doc, params, margin, contentW) {
  const S = params.static;
  const cols = htPdfColWidthsMm(contentW);
  const xs = colStarts(margin, cols);
  const xB = xs[0];
  const xC = xs[1];
  const xD = xs[2];
  const xE = xs[3];
  const xEnd = xs[5];
  const wFull = xEnd - xB;
  const wLogoZone = xD - xB;
  const wBodyText = xEnd - xB - PAD * 2;

  let y = margin;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SM);

  const headerTop = y;
  const xDText = xD + PAD;
  const xEText = xE + PAD;
  const colDTextW = xE - xD - PAD * 2;
  const colETextW = xs[4] - xE - PAD * 2;
  const logoBoxH = 14;
  const logoBoxW = Math.min(wLogoZone - 2, 42);
  let logoW = logoBoxW;
  let logoH = logoW / HT_BIOSETA_LOGO_ASPECT;
  if (logoH > logoBoxH) {
    logoH = logoBoxH;
    logoW = logoH * HT_BIOSETA_LOGO_ASPECT;
  }
  const empresaLines = 5;
  const headerH = Math.max(5 + empresaLines * 4.35 + 1.5, logoBoxH + 7);

  drawRect(doc, xB, headerTop, wFull, headerH);
  doc.addImage(
    htBiosetaLogo,
    'PNG',
    xB + 2.8,
    headerTop + 4 + (logoBoxH - logoH) / 2,
    logoW,
    logoH
  );

  let hy = headerTop + 5;
  const empresa = [
    [S.HT_EMPRESA_HEADER.d7, S.HT_EMPRESA_HEADER.e7],
    [S.HT_EMPRESA_HEADER.d8, S.HT_EMPRESA_HEADER.e8],
    [S.HT_EMPRESA_HEADER.d9, S.HT_EMPRESA_HEADER.e9],
    [S.HT_EMPRESA_HEADER.d10, S.HT_EMPRESA_HEADER.e10],
    [S.HT_EMPRESA_HEADER.d11, S.HT_EMPRESA_HEADER.e11],
  ];
  empresa.forEach(([d, e]) => {
    doc.text(safe(d), xDText, hy, { maxWidth: colDTextW });
    doc.text(safe(e), xEText, hy, { maxWidth: colETextW });
    hy += 4.35;
  });

  y = headerTop + headerH;

  const titleBlockH = 18;
  const wTitle = xE - xC;
  const wNr = xEnd - xE;
  const titleMidY = y + titleBlockH / 2;
  drawRect(doc, xB, y, wFull, titleBlockH);
  doc.line(xE, y, xE, y + titleBlockH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT);
  const titlePt = doc.splitTextToSize(S.HT_TITULO_PT.trim(), wTitle - 3);
  const titleEn = doc.splitTextToSize(S.HT_TITULO_EN.trim(), wTitle - 3);
  doc.text(titlePt, xC + wTitle / 2, y + 4.5, { align: 'center' });
  doc.text(titleEn, xC + wTitle / 2, titleMidY + 4.5, { align: 'center' });
  doc.text(safe(params.certificadoNrLine), xE + wNr / 2, titleMidY + 1, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SM);

  y += titleBlockH;

  const introH = measureHtIntroPdf(doc, S, wBodyText);
  doc.line(xB, y, xEnd, y);
  doc.line(xB, y, xB, y + introH);
  doc.line(xEnd, y, xEnd, y + introH);
  drawHtIntroPdf(doc, S, xB + PAD, y, wBodyText);
  y += introH;

  y = drawProductsSectionPdf(doc, params, S, xs, y);
  y = drawLabelValueRowsPdf(doc, params, S, xs, y);
  y = drawHtModalitySectionPdf(doc, S, xs, y);
  y = drawHtTreatmentBlockPdf(doc, params, xs, y);
  y = drawHtSectionTitleRowPdf(doc, xs, y, 44, S.HT_SHIPPING_DESC);
  y = drawHtShippingBlockPdf(doc, params, xs, y);
  return drawHtFooterPdf(doc, params, xs, y);
}
